"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  addToContentQueue,
  createScheduleBlock,
  createTask,
  getContentQueue,
  updateContentQueueItem,
  updateTask,
  type ContentQueueItem,
} from "@/lib/api";
import type { ReviewItem } from "@/lib/api/projection-types";
import { getTodayPlacement } from "@/lib/api/today";
import { useReviewProjection } from "@/hooks/useReviewProjection";
import { useDataRefresh } from "@/lib/store";
import {
  useCrossSurfaceConfirmationStore,
  type CrossSurfaceConfirmationTruth,
  type CrossSurfaceRevealTarget,
} from "@/lib/stores/crossSurfaceConfirmation";
import { invalidateProjectionStores } from "@/lib/stores/projectionInvalidation";
import {
  buildContentScheduleTitle,
  buildContentTaskDescription,
  buildContentTaskTitle,
  buildPlacementConfirmation,
  formatTime,
} from "@/components/review/reviewActionHelpers";
import { compactSurfaceTitle, sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";
import { buildBulkSummary, type BulkFailure } from "./bulkSummary";
import {
  libraryTypeKey,
  type LibrarySortMode,
  type LibraryViewMode,
} from "@/lib/libraryFormatters";
import {
  fallbackResourceDetail,
  placeCore,
  type PlacementChoice,
  type PlaceCoreResult,
} from "@/components/review/library/placeCore";
import LibraryToolbar from "@/components/review/library/LibraryToolbar";
import LibraryBulkBar from "@/components/review/library/LibraryBulkBar";
import LibraryItemList from "@/components/review/library/LibraryItemList";
import LibraryDetailModal from "@/components/review/library/LibraryDetailModal";
import LibraryEditModal, {
  type LibraryEditPayload,
} from "@/components/review/library/LibraryEditModal";
import LibraryCreateModal, {
  type LibraryCreatePayload,
} from "@/components/review/library/LibraryCreateModal";
import LibraryGoalPicker from "@/components/review/library/LibraryGoalPicker";
import ScheduleResourcePicker, {
  type ScheduleResourceParams,
} from "@/components/review/library/ScheduleResourcePicker";
import { useChatRevealHighlight } from "@/lib/useChatRevealHighlight";

type BusyAction = "do_next" | "place_today" | "bulk";

interface Props {
  onNavigate: (tab: string, initialMessage?: string) => void;
}

export default function BacklogScreen({ onNavigate }: Props) {
  const { projection, status, error, refresh } = useReviewProjection();
  const notifyTaskChange = useDataRefresh((state) => state.notifyTaskChange);
  const notifyScheduleChange = useDataRefresh((state) => state.notifyScheduleChange);
  const showCrossSurfaceConfirmation = useCrossSurfaceConfirmationStore((state) => state.showConfirmation);
  const triggerReveal = useCrossSurfaceConfirmationStore((state) => state.triggerReveal);

  const [resourceDetails, setResourceDetails] = useState<Record<string, ContentQueueItem>>({});
  const [detailError, setDetailError] = useState<string | null>(null);
  const [busyState, setBusyState] = useState<{ id: string; action: BusyAction } | null>(null);
  const [localNotice, setLocalNotice] = useState<string | null>(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [detailItemId, setDetailItemId] = useState<string | null>(null);
  const [scheduleItemId, setScheduleItemId] = useState<string | null>(null);
  const [editItemId, setEditItemId] = useState<string | null>(null);
  const [creatingReference, setCreatingReference] = useState(false);
  const [goalPickerItemId, setGoalPickerItemId] = useState<string | null>(null);

  // Library toolbar / filter / view state
  const [view, setView] = useState<LibraryViewMode>("grid");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<LibrarySortMode>("recent");
  const [filterArea, setFilterArea] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");
  const [filterRecommended, setFilterRecommended] = useState(false);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const loadResourceDetails = useCallback(async () => {
    try {
      const response = await getContentQueue("all", undefined, 100);
      const detailMap = Object.fromEntries(response.items.map((item) => [item.id, item]));
      setResourceDetails(detailMap);
      setDetailError(null);
    } catch (loadError) {
      setDetailError(
        loadError instanceof Error ? loadError.message : "Could not load saved-resource details."
      );
    }
  }, []);

  useEffect(() => {
    void loadResourceDetails();
    // BUG_EVIDENCE_PACKET_2026-05-25 follow-up: the visible Library
    // list is driven by useReviewProjection's cached projection.
    // Without a fresh refresh on mount, items captured from other
    // surfaces (Today capture popup, chat) don't show up here even
    // though their details landed in /api/queue moments earlier.
    // Pull the projection fresh on every mount.
    void refresh();
  }, [loadResourceDetails, refresh]);

  // Also re-fetch when a task or schedule change is signalled —
  // these can be downstream of a chat/capture mutation that also
  // touched the content queue (e.g. capture routed as
  // schedule_block creates a content_queue row plus a schedule
  // block). Mirrors the GoalsScreen / DecisionThreadsScreen
  // subscriptions shipped in earlier commits.
  const lastTaskChange = useDataRefresh((s) => s.lastTaskChange);
  const lastScheduleChange = useDataRefresh((s) => s.lastScheduleChange);
  useEffect(() => {
    if (!lastTaskChange && !lastScheduleChange) return;
    void refresh();
    void loadResourceDetails();
  }, [lastTaskChange, lastScheduleChange, loadResourceDetails, refresh]);

  useEffect(() => {
    if (!localNotice) return;
    const timeoutId = window.setTimeout(() => setLocalNotice(null), 4200);
    return () => window.clearTimeout(timeoutId);
  }, [localNotice]);

  const syncAfterWrite = useCallback(async () => {
    invalidateProjectionStores(["today", "plan", "review", "assistant"]);
    await Promise.all([refresh(), loadResourceDetails()]);
  }, [loadResourceDetails, refresh]);

  const revealViaConfirmation = useCallback(
    (input: {
      message: string;
      ctaLabel?: string | null;
      ctaTab?: string | null;
      revealTarget?: CrossSurfaceRevealTarget | null;
      truth?: CrossSurfaceConfirmationTruth | null;
      autoNavigate?: boolean;
    }) => {
      showCrossSurfaceConfirmation({
        message: input.message,
        ctaLabel: input.ctaLabel ?? null,
        ctaTab: input.ctaTab ?? null,
        revealTarget: input.revealTarget ?? null,
        truth: input.truth ?? null,
      });
      if (input.autoNavigate && input.ctaTab) {
        if (input.revealTarget) {
          triggerReveal();
        }
        onNavigate(input.ctaTab);
      }
    },
    [onNavigate, showCrossSurfaceConfirmation, triggerReveal]
  );

  const handleDismiss = useCallback(async (item: ReviewItem) => {
    setDismissedIds((prev) => new Set(prev).add(item.id));
    setSelectedIds((prev) => {
      if (!prev.has(item.id)) return prev;
      const next = new Set(prev);
      next.delete(item.id);
      return next;
    });
    try {
      if (item.item_type === "task") {
        await updateTask(item.id, { status: "skipped" });
        notifyTaskChange();
      } else {
        await updateContentQueueItem(item.id, { status: "archived" });
      }
      setLocalNotice(`Removed "${sanitizeSurfaceTitle(item.title).slice(0, 30)}"`);
    } catch {
      setDismissedIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      setLocalNotice("Could not remove that item.");
    }
  }, [notifyTaskChange]);

  const runPlaceCore = useCallback(
    (item: ReviewItem, choice: PlacementChoice): Promise<PlaceCoreResult> =>
      placeCore(item, choice, { resourceDetails, notifyTaskChange, notifyScheduleChange }),
    [resourceDetails, notifyTaskChange, notifyScheduleChange],
  );

  const handlePlaceResource = useCallback(
    async (item: ReviewItem, choice: PlacementChoice) => {
      setBusyState({ id: item.id, action: "place_today" });
      setLocalNotice(null);
      try {
        const result = await runPlaceCore(item, choice);
        const detail = resourceDetails[item.id] ?? fallbackResourceDetail(item);
        const itemTitle = compactSurfaceTitle(detail.title);

        if (result.scheduleBlockId && result.scheduledStart && result.scheduledEnd) {
          await syncAfterWrite();
          revealViaConfirmation({
            message: `Placed ${itemTitle} into Plan at ${formatTime(result.scheduledStart)} - ${formatTime(result.scheduledEnd)}.`,
            ctaLabel: "Reveal item",
            ctaTab: "schedule-plan",
            revealTarget: {
              surface: "schedule-plan",
              kind: "schedule_block",
              blockId: result.scheduleBlockId,
              planDate: result.plannedDate,
            },
            truth: {
              targetKind: "schedule_block",
              targetId: result.scheduleBlockId,
              destinationLayer: "plan",
              owningService: "schedule",
            },
          });
          setDetailItemId(null);
          return;
        }

        const placement = await getTodayPlacement(result.taskId).catch(() => null);
        await syncAfterWrite();
        revealViaConfirmation(
          buildPlacementConfirmation(placement, {
            restart:
              choice === "now"
                ? `${itemTitle} is now the restart in Today.`
                : `${itemTitle} is now placed for later in Today.`,
            queue:
              choice === "later"
                ? `Saved ${itemTitle} as Later so it is out of today’s pressure.`
                : `Added ${itemTitle} to Today and kept the resource here for reference.`,
            plan: `Placed ${itemTitle} into Plan for today.`,
            fallback:
              choice === "later"
                ? `Saved ${itemTitle} for later.`
                : `Added ${itemTitle} for today, but left it unscheduled.`,
          }, {
            targetKind: "task",
            targetId: result.taskId,
            owningService: "tasks",
          })
        );
        setDetailItemId(null);
      } catch (actionError) {
        setLocalNotice(
          actionError instanceof Error
            ? actionError.message
            : `Could not place ${compactSurfaceTitle(item.title)} ${choice} yet.`
        );
      } finally {
        setBusyState(null);
      }
    },
    [runPlaceCore, resourceDetails, revealViaConfirmation, syncAfterWrite]
  );

  const handleScheduleResource = useCallback(
    async (itemId: string, params: ScheduleResourceParams) => {
      const item = [
        ...(projection?.backlog?.ready_now ?? []),
        ...(projection?.backlog?.worth_parking ?? []),
      ].find((i) => i.id === itemId);
      if (!item) return;
      const detail = resourceDetails[itemId] ?? fallbackResourceDetail(item);
      const [hours, minutes] = params.startTime.split(":").map(Number);
      const start = new Date(`${params.date}T00:00:00`);
      start.setHours(hours, minutes, 0, 0);
      const end = new Date(start.getTime() + params.durationMin * 60 * 1000);
      setBusyState({ id: itemId, action: "place_today" });
      setLocalNotice(null);
      try {
        const task = await createTask({
          title: buildContentTaskTitle(detail),
          description: buildContentTaskDescription(detail),
          goal_id: detail.goal_id || undefined,
          commitment_level: "planned",
          estimated_minutes: params.durationMin,
          tags: [detail.content_type || "resource", "review-backlog"],
        });
        notifyTaskChange();
        const block = await createScheduleBlock({
          kind: "custom",
          title: buildContentScheduleTitle(detail),
          description: buildContentTaskDescription(detail),
          commitment_level: "planned",
          task_id: task.id,
          goal_id: detail.goal_id || undefined,
          goal_title: detail.goal_title || undefined,
          life_area: params.lifeArea || undefined,
          color: "#D39A55",
          start_time: start.toISOString(),
          end_time: end.toISOString(),
        });
        notifyScheduleChange();
        await syncAfterWrite();
        revealViaConfirmation({
          message: `Scheduled ${compactSurfaceTitle(detail.title)} for ${formatTime(start)} - ${formatTime(end)}.`,
          ctaLabel: "Reveal item",
          ctaTab: "schedule-plan",
          revealTarget: {
            surface: "schedule-plan",
            kind: "schedule_block",
            blockId: block.id,
            planDate: params.date,
          },
          truth: {
            targetKind: "schedule_block",
            targetId: block.id,
            destinationLayer: "plan",
            owningService: "schedule",
          },
        });
        setScheduleItemId(null);
        setDetailItemId(null);
      } catch {
        setLocalNotice(`Could not schedule ${compactSurfaceTitle(detail.title)}. Try again.`);
      } finally {
        setBusyState(null);
      }
    },
    [
      notifyScheduleChange,
      notifyTaskChange,
      projection,
      resourceDetails,
      revealViaConfirmation,
      syncAfterWrite,
    ],
  );

  // ──────────────────────────── Edit / Link goal ────────────────────────────

  // BUG_EVIDENCE_PACKET_2026-05-25 B-LIB-01: direct Add Reference flow.
  const handleCreateReference = useCallback(
    async (payload: LibraryCreatePayload) => {
      setBusyState({ id: "__create__", action: "place_today" });
      setLocalNotice(null);
      try {
        const created = await addToContentQueue({
          title: payload.title,
          description: payload.description,
          url: payload.url,
          content_type: payload.content_type || "note",
          duration_min: payload.duration_min,
        });
        await syncAfterWrite();
        setLocalNotice(
          `Added "${created.title}" to your Library.`
        );
        setCreatingReference(false);
      } catch (err) {
        setLocalNotice(
          err instanceof Error
            ? err.message
            : "Could not add to Library. Try again."
        );
      } finally {
        setBusyState(null);
      }
    },
    [syncAfterWrite],
  );

  const handleSaveEdit = useCallback(
    async (itemId: string, payload: LibraryEditPayload) => {
      setBusyState({ id: itemId, action: "place_today" });
      setLocalNotice(null);
      try {
        await updateContentQueueItem(itemId, payload);
        await syncAfterWrite();
        setLocalNotice("Saved changes.");
        setEditItemId(null);
      } catch (err) {
        setLocalNotice(
          err instanceof Error ? err.message : "Could not save changes. Try again."
        );
      } finally {
        setBusyState(null);
      }
    },
    [syncAfterWrite],
  );

  const handleSaveGoal = useCallback(
    async (itemId: string, goalId: string | null) => {
      setBusyState({ id: itemId, action: "place_today" });
      setLocalNotice(null);
      try {
        await updateContentQueueItem(itemId, { goal_id: goalId ?? "" });
        await syncAfterWrite();
        setLocalNotice(goalId ? "Linked to goal." : "Unlinked from goal.");
        setGoalPickerItemId(null);
      } catch (err) {
        setLocalNotice(
          err instanceof Error ? err.message : "Could not link to goal. Try again."
        );
      } finally {
        setBusyState(null);
      }
    },
    [syncAfterWrite],
  );

  // ──────────────────────────── Selection ────────────────────────────

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const backlog = projection?.backlog;

  const allItems = useMemo(() => {
    const readyNow = backlog?.ready_now ?? [];
    const worthParking = backlog?.worth_parking ?? [];
    return [...readyNow, ...worthParking].filter((i) => !dismissedIds.has(i.id));
  }, [backlog, dismissedIds]);

  // Recommended = first item per goal (existing topNextStepIds rule).
  const topNextStepIds = useMemo(() => {
    const seenGoals = new Set<string>();
    const ids = new Set<string>();
    for (const it of allItems) {
      if (it.goal_id && !seenGoals.has(it.goal_id)) {
        seenGoals.add(it.goal_id);
        ids.add(it.id);
      }
    }
    return ids;
  }, [allItems]);

  const counts = useMemo(() => {
    const byArea: Record<string, number> = {};
    const byType: Record<string, number> = {};
    let recommended = 0;
    for (const it of allItems) {
      if (it.goal_category) byArea[it.goal_category] = (byArea[it.goal_category] || 0) + 1;
      const tk = libraryTypeKey(it.content_type);
      byType[tk] = (byType[tk] || 0) + 1;
      if (topNextStepIds.has(it.id)) recommended += 1;
    }
    return { byArea, byType, recommended, total: allItems.length };
  }, [allItems, topNextStepIds]);

  const filteredItems = useMemo(() => {
    let list = allItems;
    if (filterArea !== "all") list = list.filter((i) => i.goal_category === filterArea);
    if (filterType !== "all") list = list.filter((i) => libraryTypeKey(i.content_type) === filterType);
    if (filterRecommended) list = list.filter((i) => topNextStepIds.has(i.id));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((i) => {
        const title = i.title.toLowerCase();
        const reason = (resourceDetails[i.id]?.priority_reason ?? "").toLowerCase();
        // BUG_EVIDENCE_PACKET_2026-05-25 LIBRARY-DECISION: also match
        // the canonical URL and the description. Without this, a
        // reference saved from chat/capture whose title got LLM-
        // rewritten ("Saved article from example.com") wouldn't
        // surface for a user (or strict-UAT) typing the URL slug.
        const detail = resourceDetails[i.id];
        const url = (detail?.url ?? "").toLowerCase();
        const description = (detail?.description ?? "").toLowerCase();
        return (
          title.includes(q) ||
          reason.includes(q) ||
          url.includes(q) ||
          description.includes(q)
        );
      });
    }
    if (sort === "alpha") {
      list = [...list].sort((a, b) => a.title.localeCompare(b.title));
    } else if (sort === "area") {
      list = [...list].sort((a, b) =>
        (a.goal_category ?? "zzz").localeCompare(b.goal_category ?? "zzz"),
      );
    }
    return list;
  }, [allItems, filterArea, filterType, filterRecommended, search, sort, topNextStepIds, resourceDetails]);

  const recommendedItems = filteredItems.filter((i) => topNextStepIds.has(i.id));
  const savedItems = filteredItems.filter((i) => !topNextStepIds.has(i.id));
  const chatRevealLibraryKey = useMemo(
    () => filteredItems.map((item) => item.id).join("|"),
    [filteredItems],
  );
  useChatRevealHighlight("library", chatRevealLibraryKey);

  // ──────────────────────────── Bulk actions ────────────────────────────

  const runBulk = useCallback(
    async (
      action: "archive" | "todo" | "schedule",
      label: string,
    ) => {
      if (selectedIds.size === 0) return;
      const targets = filteredItems.filter((i) => selectedIds.has(i.id));
      setBusyState({ id: "__bulk__", action: "bulk" });
      setLocalNotice(null);
      let ok = 0;
      // DECIDE-LIBRARY-001: keep WHICH item failed and WHY, not just a count,
      // so the notice can name what to retry instead of an opaque "(N failed)".
      const failures: BulkFailure[] = [];
      for (const item of targets) {
        try {
          if (action === "archive") {
            if (item.item_type === "task") {
              await updateTask(item.id, { status: "skipped" });
            } else {
              await updateContentQueueItem(item.id, { status: "archived" });
            }
            setDismissedIds((prev) => new Set(prev).add(item.id));
          } else if (action === "todo") {
            await runPlaceCore(item, "now");
          } else if (action === "schedule") {
            await runPlaceCore(item, "planned");
          }
          ok += 1;
        } catch (e) {
          failures.push({
            id: item.id,
            title: compactSurfaceTitle(item.title),
            error: e instanceof Error ? e.message : undefined,
          });
        }
      }
      notifyTaskChange();
      notifyScheduleChange();
      await syncAfterWrite();
      clearSelection();
      setBusyState(null);
      setLocalNotice(buildBulkSummary(label, ok, failures));
    },
    [
      clearSelection,
      filteredItems,
      notifyScheduleChange,
      notifyTaskChange,
      runPlaceCore,
      selectedIds,
      syncAfterWrite,
    ],
  );

  // ──────────────────────────── Render ────────────────────────────

  if ((status === "loading" || status === "idle") && !projection) {
    return (
      <div className="flex justify-center py-14">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-forest/25 border-t-forest-med" />
      </div>
    );
  }

  if (status === "error" && !projection) {
    return (
      <div className="rounded-[28px] border border-amber/25 bg-card px-5 py-6 text-center">
        <p className="text-lg font-semibold text-text">Library is not ready yet</p>
        <p className="mt-2 text-sm text-text-light">{error || "Failed to load Library."}</p>
      </div>
    );
  }

  const hasAnything = allItems.length > 0;
  const detailItem = detailItemId ? allItems.find((i) => i.id === detailItemId) : null;
  const scheduleItem = scheduleItemId ? allItems.find((i) => i.id === scheduleItemId) : null;
  const selecting = selectedIds.size > 0;
  const bulkBusy = busyState?.action === "bulk";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted">
            Decisionease · Saved
          </p>
          <h2 className="mt-1 text-2xl font-semibold text-forest">Library</h2>
        </div>
        <p className="text-xs text-muted">
          {counts.total} saved · {counts.recommended} recommended
        </p>
      </div>

      <LibraryToolbar
        search={search}
        onSearch={setSearch}
        view={view}
        onViewChange={setView}
        sort={sort}
        onSortChange={setSort}
        filterArea={filterArea}
        onAreaChange={setFilterArea}
        filterType={filterType}
        onTypeChange={setFilterType}
        filterRecommended={filterRecommended}
        onRecommendedToggle={() => setFilterRecommended((v) => !v)}
        areaCounts={counts.byArea}
        typeCounts={counts.byType}
        onAddReference={() => setCreatingReference(true)}
      />

      <LibraryBulkBar
        count={selectedIds.size}
        busy={bulkBusy}
        onSchedule={() => void runBulk("schedule", "Scheduled")}
        onAddToTodos={() => void runBulk("todo", "Added to today —")}
        onArchive={() => void runBulk("archive", "Archived")}
        onClear={clearSelection}
      />

      {localNotice && (
        <div
          data-testid="review-backlog-notice"
          className="rounded-2xl border border-sage/40 bg-sage-pale/60 px-4 py-2.5 text-sm text-forest-med"
        >
          {localNotice}
        </div>
      )}

      {detailError && (
        <div className="rounded-2xl border border-amber/25 bg-sand-pale px-4 py-2.5 text-sm text-text-light">
          {detailError}
        </div>
      )}

      {!hasAnything && (
        <div className="rounded-[28px] border border-dashed border-border bg-card px-5 py-12 text-center">
          <p className="text-lg font-semibold text-text">Nothing in your library yet</p>
          <p className="mt-2 text-sm text-text-light">
            Save articles, notes, and links from chat — they’ll appear here, tied to a life area or goal.
          </p>
        </div>
      )}

      {hasAnything && filteredItems.length === 0 && (
        <div className="rounded-2xl border border-dashed border-border bg-card px-5 py-10 text-center">
          <p className="text-base font-semibold text-text">No matches</p>
          <p className="mt-1 text-sm text-text-light">Try a different filter, type, or search term.</p>
        </div>
      )}

      {recommendedItems.length > 0 && (
        <Section title="Recommended for you" count={recommendedItems.length}>
          <LibraryItemList
            items={recommendedItems}
            resourceDetails={resourceDetails}
            recommended
            view={view}
            selectedIds={selectedIds}
            selecting={selecting}
            busyId={busyState?.id ?? null}
            onToggleSelect={toggleSelect}
            onOpenDetail={setDetailItemId}
            onEdit={setEditItemId}
            onLinkGoal={setGoalPickerItemId}
            onAddToTodo={(it) => void handlePlaceResource(it, "now")}
            onAddToSchedule={(it) => setScheduleItemId(it.id)}
            onDismiss={(it) => void handleDismiss(it)}
          />
        </Section>
      )}

      {savedItems.length > 0 && (
        <Section title="All saved" count={savedItems.length}>
          <LibraryItemList
            items={savedItems}
            resourceDetails={resourceDetails}
            recommended={false}
            view={view}
            selectedIds={selectedIds}
            selecting={selecting}
            busyId={busyState?.id ?? null}
            onToggleSelect={toggleSelect}
            onOpenDetail={setDetailItemId}
            onEdit={setEditItemId}
            onLinkGoal={setGoalPickerItemId}
            onAddToTodo={(it) => void handlePlaceResource(it, "now")}
            onAddToSchedule={(it) => setScheduleItemId(it.id)}
            onDismiss={(it) => void handleDismiss(it)}
          />
        </Section>
      )}

      {detailItem && (
        <LibraryDetailModal
          item={detailItem}
          detail={resourceDetails[detailItem.id]}
          recommended={topNextStepIds.has(detailItem.id)}
          busy={busyState?.id === detailItem.id}
          onClose={() => setDetailItemId(null)}
          onAddToTodo={(it) => void handlePlaceResource(it, "now")}
          onAddToSchedule={(it) => {
            setScheduleItemId(it.id);
            setDetailItemId(null);
          }}
        />
      )}

      {scheduleItem && (
        <ScheduleResourcePicker
          itemTitle={sanitizeSurfaceTitle(scheduleItem.title)}
          defaultDurationMin={resourceDetails[scheduleItem.id]?.duration_min ?? 30}
          defaultLifeArea={scheduleItem.goal_category ?? null}
          onCancel={() => setScheduleItemId(null)}
          onSubmit={(params) => void handleScheduleResource(scheduleItem.id, params)}
          isBusy={busyState?.id === scheduleItem.id}
        />
      )}

      {editItemId && resourceDetails[editItemId] && (
        <LibraryEditModal
          detail={resourceDetails[editItemId]}
          isBusy={busyState?.id === editItemId}
          onCancel={() => setEditItemId(null)}
          onSubmit={(payload) => void handleSaveEdit(editItemId, payload)}
        />
      )}

      {creatingReference && (
        <LibraryCreateModal
          isBusy={busyState?.id === "__create__"}
          onCancel={() => setCreatingReference(false)}
          onSubmit={(payload) => void handleCreateReference(payload)}
        />
      )}

      {goalPickerItemId && (() => {
        const item = allItems.find((i) => i.id === goalPickerItemId);
        if (!item) return null;
        const detail = resourceDetails[goalPickerItemId];
        return (
          <LibraryGoalPicker
            itemTitle={sanitizeSurfaceTitle(item.title)}
            currentGoalId={detail?.goal_id ?? item.goal_id ?? null}
            isBusy={busyState?.id === goalPickerItemId}
            onCancel={() => setGoalPickerItemId(null)}
            onSubmit={(goalId) => void handleSaveGoal(goalPickerItemId, goalId)}
          />
        );
      })()}
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-3">
        <h3 className="text-base font-semibold text-text">{title}</h3>
        <span className="h-px flex-1 bg-border" />
        <span className="rounded-full bg-bg px-2 py-0.5 text-[10px] font-semibold text-muted">
          {count}
        </span>
      </div>
      {children}
    </section>
  );
}
